import { z } from 'zod';

// Enum definition for FootageType
export enum FootageType {
  Online = 'online',
  Personal = 'personal',
}

export type FileWithType = File | {
  type: string;
  name: string;
  size: number;
};

const isFile = (value: unknown): value is File | FileWithType => {
  if (typeof window !== 'undefined' && value instanceof File) return true;
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    'name' in value &&
    'size' in value
  );
};

// Supported image types (ensure this matches your actual supported types)
const supportedImgTypes = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4', 'video/avi'];

// Schema for online media sources where eventFootage is optional
const OnlineEvidenceSchema = z.object({
  mediaSource: z.literal(FootageType.Online),
  eventFootage: z
    .array(z.custom<File | FileWithType>((data) => isFile(data)))
    .optional()
    .refine(
      (files) => !files || files.every(file => supportedImgTypes.includes(file.type)),
      {
        message: 'Unsupported file type detected',
      }
    ),
  eventDate: z.string().nonempty('Event date is required'),
  location: z.string().min(1, 'Location is required'),
  university: z.string().optional(),
  url: z.string().url({
    message: "Invalid URL format"
  }).min(1, { message: "URL is required" }),
  description: z.string().optional(),
  incidentType: z.string().min(1, 'Incident Type is required'),
});

// Schema for personal media sources where eventFootage is required
const PersonalEvidenceSchema = z.object({
  mediaSource: z.literal(FootageType.Personal),
  eventFootage: z
    .array(z.custom<File | FileWithType>((data) => isFile(data)))
    .min(1, 'At least one file is required')
    .refine(
      (files) => files.every(file => supportedImgTypes.includes(file.type)),
      {
        message: 'Unsupported file type detected',
      }
    ),
  eventDate: z.string().nonempty('Event date is required'),
  location: z.string().min(1, 'Location is required'),
  university: z.string().optional(),
  description: z.string().optional(),
  incidentType: z.string().min(1, 'Incident Type is required'),
});

// Combined Evidence Schema using discriminated union
const EvidenceSchema = z.discriminatedUnion('mediaSource', [
  OnlineEvidenceSchema,
  PersonalEvidenceSchema,
]);

// Overall Report Event Schema
export const ReportEventSchema = z.object({
  evidence: z
    .array(EvidenceSchema)
    .min(1, 'At least one incident is required'),
  email: z
    .string()
    .email('Invalid email address')
    .optional()
    .or(z.literal('')), // Allow empty string if email is not required
  // 'token' will be appended later
});